import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
	getAvailableLocales,
	getLocaleSettings,
	getUserLocalePreference,
	setUserLocalePreference,
	getUserPreferenceFor,
	setUserPreferenceFor,
} from "services/localisation";
import { applyResolvedLocale, hasSiteAppSession } from "i18n";
import { showError } from "../redux/common/actions";

const DEFAULT_LOCALE = "en";
// Represents "no preference saved" - the site app's own default language wins.
export const UNSET_PREFERENCE = "";

// The preference endpoints require a siteApp-context token, so nothing is
// fetched (enabled=false) outside an active site app session - portal and
// client/admin screens are fixed English.
const useUserLocalePreference = ({ targetUserId } = {}) => {
	const reduxDispatch = useDispatch();
	const { t } = useTranslation();

	const enabled = hasSiteAppSession();

	const [available, setAvailable] = useState([DEFAULT_LOCALE]);
	const [siteDefault, setSiteDefault] = useState(DEFAULT_LOCALE);
	const [selected, setSelected] = useState(UNSET_PREFERENCE);
	const [busy, setBusy] = useState(true);

	useEffect(() => {
		if (!hasSiteAppSession()) {
			setBusy(false);
			return;
		}

		let active = true;

		(async () => {
			const [localesRes, settingsRes, prefRes] = await Promise.all([
				getAvailableLocales(),
				getLocaleSettings(),
				targetUserId
					? getUserPreferenceFor(targetUserId)
					: getUserLocalePreference(),
			]);

			if (!active) return;

			if (localesRes?.status)
				setAvailable(localesRes.data ?? [DEFAULT_LOCALE]);
			if (settingsRes?.status)
				setSiteDefault(settingsRes.data?.defaultLocale ?? DEFAULT_LOCALE);
			setSelected(
				prefRes?.status && prefRes.data?.locale
					? prefRes.data.locale
					: UNSET_PREFERENCE
			);
			setBusy(false);
		})();

		return () => {
			active = false;
		};
	}, [targetUserId]);

	const save = useCallback(
		async (value) => {
			if (value === selected) return;
			setBusy(true);

			const response = targetUserId
				? await setUserPreferenceFor(targetUserId, value || null)
				: await setUserLocalePreference(value || null);
			if (!response?.status) {
				setBusy(false);
				reduxDispatch(
					showError(
						typeof response?.data === "string"
							? response.data
							: t("message.failedToSaveLanguagePreference")
					)
				);
				return;
			}

			setSelected(value);
			if (!targetUserId) {
				// Apply the locale the server already resolved for the active site app
				// (gated by its enabled locales, or its default when unset) immediately,
				// instead of waiting for a route change to re-resolve it.
				await applyResolvedLocale(response.data?.resolvedLocale);
			}
			setBusy(false);
		},
		[selected, reduxDispatch, t, targetUserId]
	);

	return { enabled, available, siteDefault, selected, busy, save };
};

export default useUserLocalePreference;
